import React, { useState, useRef, useEffect } from "react";
import { useSpring, animated } from "react-spring";
import { IoIosArrowDown } from "react-icons/io";
import { cn } from "../utils/cn";

interface SelectOptionProps {
  label: string;
  options: string[];
  onOptionChange?: (selectedOption: string) => void;
  className?: string;
}

const SelectOption: React.FC<SelectOptionProps> = ({ label, options, onOptionChange, className }) => {
  const [selectedOption, setSelectedOption] = useState("");
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const [isInputFocused, setInputFocused] = useState(false);

  const inputRef = useRef<HTMLDivElement>(null);

  const isFocused = isInputFocused || selectedOption !== "";

  const { fontSize, top } = useSpring({
    fontSize: isFocused ? '12px' : '14px',
    top: isFocused ? '5px' : '14px',
  });

  const handleLabelClick = () => {
    setInputFocused(true);
    setDropdownOpen(!isDropdownOpen);
  };

  const handleOptionClick = (option: string) => {
    setSelectedOption(option);
    setDropdownOpen(false);
    onOptionChange && onOptionChange(option);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
      setDropdownOpen(false);
      setInputFocused(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div ref={inputRef} className={cn(["h-[55px] rounded-xl w-full bg-white relative border-2 border-[#EAECF0]", className])} onClick={handleLabelClick}>
      <animated.div
        style={{ fontSize, top, position: 'absolute' }}
        className="font-medium text-[#9392A2] cursor-pointer px-3"
      >
        {label}
      </animated.div>
      <div className="absolute right-2.5 top-5">
        <IoIosArrowDown className="text-[#9392A2]" />
      </div>
      <div
        className="absolute top-[19px] left-0 right-0 text-sm font-medium text-black outline-none px-3 pt-1 cursor-pointer"
        onFocus={() => setInputFocused(true)}
        onBlur={() => setInputFocused(false)}
      >
        {selectedOption || " "}
      </div>
      {isDropdownOpen && (
        <div className="absolute top-full left-0 w-full h-auto mt-1 bg-white border border-[#EAECF0] rounded-b-xl z-50">
          {options.map(option => (
            <div
              key={option}
              className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-100"
              onClick={() => handleOptionClick(option)}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectOption;
